"use client"

import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: { email: string; name: string; displayName: string; id?: number } | null
  logout: () => Promise<void>
  fetchWithAuth: typeof fetch
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const AUTH_CHANNEL = "cogevi-auth"

function broadcastAuth(type: "LOGIN" | "LOGOUT") {
  if (typeof window === "undefined") return
  try {
    if ("BroadcastChannel" in window) {
      const channel = new BroadcastChannel(AUTH_CHANNEL)
      channel.postMessage({ type })
      channel.close()
    } else {
      // Fallback for older Safari (< 15.4)
      window.localStorage.setItem(
        "cogevi-auth-event",
        JSON.stringify({ type, timestamp: Date.now() })
      )
      window.localStorage.removeItem("cogevi-auth-event")
    }
  } catch {
    // Ignore broadcast errors
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<AuthContextType["user"]>(null)
  const isLoggingOut = useRef(false)
  const isCheckingRef = useRef(false)
  const isRefreshingRef = useRef(false)
  const isAuthenticatedRef = useRef(isAuthenticated)
  const skipNextBroadcastRef = useRef(false)

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated
  }, [isAuthenticated])

  // Vérifier l'auth au montage
  const checkAuth = useCallback(async () => {
    if (isCheckingRef.current) return
    isCheckingRef.current = true
    try {
      let response = await fetch("/api/auth/me")

      // Si 401 et session persistante activée, essayer de rafraîchir le token
      if (response.status === 401 && document.cookie.includes("persistent_session=true")) {
        const refreshRes = await fetch("/api/auth/refresh", { method: "POST" })
        if (refreshRes.ok) {
          response = await fetch("/api/auth/me")
        }
      }

      if (response.ok) {
        const data = await response.json()
        setUser(data.user)
        setIsAuthenticated(true)
      } else {
        setUser(null)
        setIsAuthenticated(false)
      }
    } catch (error) {
      console.error("Auth check error:", error)
      setUser(null)
      setIsAuthenticated(false)
    } finally {
      setIsLoading(false)
      isCheckingRef.current = false
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Cross-tab auth sync
  useEffect(() => {
    if (typeof window === "undefined") return

    const handleMessage = (data: { type: string }) => {
      if (data.type === "LOGIN") {
        if (!isAuthenticatedRef.current) {
          skipNextBroadcastRef.current = true
          if (window.location.pathname.includes("/login")) {
            const redirect = new URLSearchParams(window.location.search).get("redirect") || "/"
            window.location.href = redirect
          } else {
            checkAuth()
          }
        }
      } else if (data.type === "LOGOUT") {
        if (isAuthenticatedRef.current) {
          skipNextBroadcastRef.current = true
          setUser(null)
          setIsAuthenticated(false)
          window.location.href = "/login"
        }
      }
    }

    let bc: BroadcastChannel | null = null
    if ("BroadcastChannel" in window) {
      bc = new BroadcastChannel(AUTH_CHANNEL)
      bc.onmessage = (event) => handleMessage(event.data)
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === "cogevi-auth-event" && event.newValue) {
        try {
          const data = JSON.parse(event.newValue)
          handleMessage(data)
        } catch {
          // Ignore malformed storage events
        }
      }
    }
    window.addEventListener("storage", handleStorage)

    return () => {
      bc?.close()
      window.removeEventListener("storage", handleStorage)
    }
  }, [checkAuth])

  // Broadcast login to other tabs
  useEffect(() => {
    if (!isAuthenticated) return
    if (skipNextBroadcastRef.current) {
      skipNextBroadcastRef.current = false
      return
    }
    broadcastAuth("LOGIN")
  }, [isAuthenticated])

  // Vérification périodique de la session (toutes les 60s)
  useEffect(() => {
    if (!isAuthenticated || isLoggingOut.current) return

    const interval = setInterval(() => {
      if (isCheckingRef.current) return
      isCheckingRef.current = true
      fetch("/api/auth/me").then((response) => {
        if (!response.ok) {
          isLoggingOut.current = true
          toast.error("Votre session a expiré. Veuillez vous reconnecter.", {
            className: "!border-red-700 !bg-red-900 !text-neutral-50",
            duration: 5000,
          })
          setUser(null)
          setIsAuthenticated(false)
          fetch("/api/auth/logout", { method: "POST" }).catch(() => {})
          window.location.href = "/login"
        }
      }).catch(() => {}).finally(() => {
        isCheckingRef.current = false
      })
    }, 60_000)

    return () => clearInterval(interval)
  }, [isAuthenticated])

  // Refresh automatique du token pour les sessions persistantes
  useEffect(() => {
    if (!isAuthenticated || isLoggingOut.current) return

    const hasPersistent = document.cookie.includes("persistent_session=true")
    if (!hasPersistent) return

    const refresh = async () => {
      if (isRefreshingRef.current) return
      isRefreshingRef.current = true
      try {
        const res = await fetch("/api/auth/refresh", { method: "POST" })
        if (!res.ok) throw new Error("Refresh failed")
      } catch {
        // Ignorer — le prochain /api/auth/me échouera et déconnectera
      } finally {
        isRefreshingRef.current = false
      }
    }

    // Rafraîchir immédiatement au cas où le token est proche de l'expiration
    refresh()

    const interval = setInterval(refresh, 30 * 60 * 1000) // 30 minutes

    return () => clearInterval(interval)
  }, [isAuthenticated])

  // Fonction de logout
  const logout = useCallback(async () => {
    skipNextBroadcastRef.current = true
    broadcastAuth("LOGOUT")
    try {
      await fetch("/api/auth/logout", { method: "POST" })
    } catch (error) {
      console.error("Logout error:", error)
    } finally {
      setUser(null)
      setIsAuthenticated(false)
      window.location.href = "/login"
    }
  }, [])

  // Wrapper fetch qui intercepte les 401
  const fetchWithAuth = useCallback<typeof fetch>(
    async (input: RequestInfo | URL, init?: RequestInit) => {
      const response = await fetch(input, init)

      // Si on reçoit un 401 et qu'on n'est pas déjà en train de se déconnecter
      if (response.status === 401 && !isLoggingOut.current) {
        isLoggingOut.current = true

        // Afficher un toast pour prévenir l'utilisateur
        toast.error("Votre session a expiré. Veuillez vous reconnecter.", {
          className: "!border-red-700 !bg-red-900 !text-neutral-50",
          duration: 5000,
        })

        // Déconnecter l'utilisateur
        setUser(null)
        setIsAuthenticated(false)

        // Appeler l'API de logout pour nettoyer les cookies
        try {
          await fetch("/api/auth/logout", { method: "POST" })
        } catch (error) {
          console.error("Logout error:", error)
        }

        // Rediriger vers le login de manière obligatoire
        window.location.href = "/login"
      }

      return response
    },
    []
  )

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        logout,
        fetchWithAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
